import React from 'react';
import { Link } from 'react-router-dom';
import { useReveal } from '../lib/useReveal';

const STEPS = [
  ['01', 'Private Enquiry', 'Share your occasion, guest numbers, style and preferred level of indulgence.'],
  ['02', 'Personal Consultation', 'We confirm preferences, dietary needs, tone of experience and pace.'],
  ['03', 'Curated Proposal', 'You receive a tailored experience outline with inclusions and investment.'],
  ['04', 'Deposit', 'Your booking is secured with a deposit.'],
  ['05', 'Concierge Planning', 'We confirm every venue, producer, transport, timing and finishing touch.'],
  ['06', 'Hosted Experience', 'You are personally hosted through the journey.'],
  ['07', 'Aftercare', 'A thank-you note, curated recommendations and producer list follow.'],
];

export default function About() {
  const ref = useReveal();
  return (
    <div ref={ref} data-testid="about-page" className="pt-36 sm:pt-44 lg:pt-56">
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12 pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 reveal">
            <div className="tdt-eyebrow mb-5">About</div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[color:var(--tdt-ink)] mb-8">
              Exceptional taste, <em className="not-italic text-[color:var(--tdt-burgundy)]">privately hosted.</em>
            </h1>
          </div>
          <div className="lg:col-span-6 reveal">
            <p className="text-base sm:text-lg lg:text-lg leading-relaxed text-[color:var(--tdt-muted)] mb-5">
              Tour de Taste creates private, high-end culinary journeys for discerning guests who value access, elegance, provenance and unforgettable hospitality.
            </p>
            <p className="text-base sm:text-lg lg:text-lg leading-relaxed text-[color:var(--tdt-muted)] mb-5">
              This is not a walking tour. It is being hosted through a beautifully designed food and wine experience that feels exclusive, effortless, and memorable.
            </p>
            <p className="text-base sm:text-lg lg:text-lg leading-relaxed text-[color:var(--tdt-muted)]">
              Every journey is shaped around the guest — their tastes, occasion, pace and desired level of indulgence — and is delivered with the composure of a private concierge and the warmth of a thoughtful host.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[color:var(--tdt-cream)] py-16 sm:py-14 sm:py-18 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12">
          <div className="reveal max-w-2xl mb-14">
            <div className="tdt-eyebrow mb-5">The Experience</div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-[color:var(--tdt-ink)]">
              A seamless journey from first enquiry to final glass.
            </h2>
          </div>
          <ol className="grid md:grid-cols-2 gap-y-10 gap-x-14">
            {STEPS.map(([n, t, b], i) => (
              <li key={n} className="reveal" data-testid={`step-${n}`}>
                <div className="flex items-baseline gap-5">
                  <span className="font-serif text-3xl text-[color:var(--tdt-gold)] leading-none">{n}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-[color:var(--tdt-ink)] mb-2">{t}</h3>
                    <p className="text-sm leading-relaxed text-[color:var(--tdt-muted)]">{b}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-[color:var(--tdt-ink)] text-[color:var(--tdt-ivory)] py-16 sm:py-14 sm:py-18 lg:py-32">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <div className="tdt-eyebrow tdt-eyebrow-light mb-5">Brand Promise</div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.05] mb-6">
              Where fine dining meets discovery.
            </h2>
            <p className="text-[color:var(--tdt-cream)] leading-relaxed">
              We open the door to carefully chosen venues, private rooms, chef-led moments and the local makers who define a destination’s culinary character. Every experience is hosted with care and quiet confidence.
            </p>
          </div>
          <div className="reveal media-card relative aspect-[4/3]">
            <img src="/assets/photos/chef-plating.jpg" alt="Chef plating" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-[color:var(--tdt-ivory)] py-16 sm:py-14 sm:py-18 lg:py-32 text-center">
        <div className="max-w-[820px] mx-auto px-5 sm:px-6 lg:px-12 reveal">
          <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-[color:var(--tdt-ink)] mb-6">
            Begin a private conversation.
          </h2>
          <Link to="/enquire" data-testid="about-enquire-btn" className="btn-pill btn-gold">Enquire Privately</Link>
        </div>
      </section>
    </div>
  );
}
